import { api } from "./client";
import type { ApiResponse } from "./types";

/**
 * The wins pipeline, seen from the operator's side.
 *
 * Three reads and one action, because the service deliberately offers nothing
 * else — no approve queue, no editing a screened row, no "publish this one
 * anyway". A queue nobody empties is the failure the whole pipeline is built
 * to avoid, and putting one behind an admin login would reintroduce it wearing
 * a badge. A bad item is retired from the announcements desk like any other.
 */

/**
 * Open strings on reads, closed only where we compare.
 *
 * Same rule as the announcements client: these vocabularies grow, and a closed
 * union turns a new server value into a crash rather than a word to display.
 * `reason` in particular gains entries whenever the editorial rule learns
 * something.
 */
export interface WinsRun {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  /** "ok" | "all_sources_failed" | … — render it, don't switch exhaustively. */
  outcome: string;
  sourcesTotal: number;
  sourcesFailed: number;
  fetched: number;
  droppedUrl: number;
  droppedStory: number;
  screened: number;
  published: number;
}

export interface WinsRunsResponse {
  /**
   * Both flags come back on every response, and the page leads with them.
   * A switched-off pipeline and a broken one produce the same empty table;
   * these are the only things that tell them apart.
   */
  enabled: boolean;
  screening: string;
  runs: WinsRun[];
}

export interface WinsScreening {
  id: string;
  title: string;
  source: string;
  url: string;
  verdict: string;
  reason: string | null;
  topic: string | null;
  confidence: number | null;
  modelVersion: string | null;
  createdAt: string;
}

export function listWinsRuns(limit = 20) {
  return api.get<ApiResponse<WinsRunsResponse>>("/announcements/wins/runs", { limit });
}

export function getWinsRunScreenings(runId: string, verdict?: "accept" | "reject") {
  return api.get<ApiResponse<{ runId: string; screenings: WinsScreening[] }>>(
    `/announcements/wins/runs/${runId}/screenings`,
    verdict ? { verdict } : undefined,
  );
}

/**
 * Run it now — forced past the switch, and it PUBLISHES.
 *
 * There is no rehearsal mode by design: a dry run that skipped the write would
 * not exercise the part most likely to break. The page confirms before calling
 * this, and the confirmation says so in those words.
 */
export function runWinsIngestNow() {
  return api.post<ApiResponse<Record<string, unknown>>>("/announcements/wins/run", {});
}

/** Human labels. Unknown values fall through to the raw string. */
export const REJECT_REASON_LABELS: Record<string, string> = {
  political_attribution: "Credited a leader or party",
  partisan: "Party politics",
  divisive: "Set communities against each other",
  state_ranking: "Ranked states against each other",
  not_an_achievement: "Not an achievement",
  low_quality: "Low quality",
  low_confidence: "Model was unsure",
};

export const OUTCOME_LABELS: Record<string, string> = {
  ok: "ok",
  all_sources_failed: "every source failed",
  disabled: "switched off mid-run",
};
